import React, { ReactNode, useId } from 'react';
import * as Styled from './FormControl.styled';
import { NotificationIntent } from '../Notification/Notification.typed';
import {
  ElementProps,
  FormControlProps,
  FormElements,
} from './FormControl.types';

import Label from '../Label/Label';
import Input from '../Input/Input';
import Select from '../Select/Select';
import Textarea from '../Textarea/Textarea';
import Checkbox from '../Checkbox/Checkbox';
import Radio from '../Radio/Radio';
import Notification from '../Notification/Notification';

import { TextareaProps } from '../Textarea/Textarea.types';
import { SelectProps } from '../Select/Select.typed';
import { CheckboxProps } from '../Checkbox/Checkbox.typed';
import { RadioProps } from '../Radio/Radio.typed';
import { LabelProps } from '../Label/Label.types';
import { InputProps } from '../Input/Input.typed';
import Datepicker from '../Datepicker/Datepicker';
import type { DatepickerProps } from '../Datepicker/Datepicker.typed';
import { MediaUploader } from '../MediaUploader/MediaUploader';
import { MediaUploaderProps } from '../MediaUploader/MediaUploader.typed';
import RadioTab from '../RadioTab/RadioTab';
import { RadioTabProps } from '../RadioTab/RadioTab.types';
import { MultiSelect } from '../MultiSelect/MultiSelect';
import { MultiSelectProps } from '../MultiSelect/MultiSelect.typed';

const getFormControlChildren = (element: FormElements, children: ReactNode) => {
  return React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === element,
  );
};

export const FormControl = ({
  id: providedId,
  children,
  disabled = false,
  required = false,
  notificationIntent = NotificationIntent.ERROR,
  showNotification = false,
  notificationMessage = 'Field required',
  leading,
  trailing,
  ...rest
}: FormControlProps) => {
  const label = getFormControlChildren(Label, children);
  const input = getFormControlChildren(Input, children);
  const textarea = getFormControlChildren(Textarea, children);
  const select = getFormControlChildren(Select, children);
  const checkbox = getFormControlChildren(Checkbox, children);
  const radio = getFormControlChildren(Radio, children);
  const radioTab = getFormControlChildren(RadioTab, children);
  const datePicker = getFormControlChildren(Datepicker, children);
  const mediaUploader = getFormControlChildren(MediaUploader, children);
  const multiSelect = getFormControlChildren(MultiSelect, children);

  const hasError =
    notificationIntent === NotificationIntent.ERROR && showNotification;

  // If no id is provided, use React's stable id generation so it matches across SSR + hydration.
  const reactGeneratedId = useId();
  const id = providedId ?? reactGeneratedId;

  const errorId = `${id}-error`;

  const labelWithProps =
    React.isValidElement(label) &&
    React.cloneElement(label as ElementProps<LabelProps>, {
      htmlFor: id,
      required: required,
    });

  const inputWithProps =
    React.isValidElement(input) &&
    React.cloneElement(input as ElementProps<InputProps>, {
      id,
      required,
      disabled,
      hasError,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const textareaWithProps =
    React.isValidElement(textarea) &&
    React.cloneElement(textarea as ElementProps<TextareaProps>, {
      id,
      required,
      disabled,
      hasError,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const selectWithProps =
    React.isValidElement(select) &&
    React.cloneElement(select as ElementProps<SelectProps>, {
      id,
      required,
      disabled,
      hasError: hasError,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const checkboxWithProps =
    React.isValidElement(checkbox) &&
    React.cloneElement(checkbox as ElementProps<CheckboxProps>, {
      id,
      required,
      disabled,
      hasError: hasError,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const radioWithProps =
    React.isValidElement(radio) &&
    React.cloneElement(radio as ElementProps<RadioProps>, {
      id,
      required,
      disabled,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const radioTabWithProps =
    React.isValidElement(radioTab) &&
    React.cloneElement(radioTab as ElementProps<RadioTabProps>, {
      id,
      required,
      disabled,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const datePickerWithProps =
    React.isValidElement(datePicker) &&
    React.cloneElement(datePicker as ElementProps<DatepickerProps>, {
      id,
      required,
      disabled,
      hasError,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const mediaUploaderWithProps =
    React.isValidElement(mediaUploader) &&
    React.cloneElement(mediaUploader as ElementProps<MediaUploaderProps>, {
      id,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const multiSelectWithProps =
    React.isValidElement(multiSelect) &&
    React.cloneElement(multiSelect as ElementProps<MultiSelectProps<never>>, {
      id,
      ...(hasError && { 'aria-errormessage': errorId, 'aria-invalid': true }),
    });

  const formElements = [
    inputWithProps,
    textareaWithProps,
    selectWithProps,
    checkboxWithProps,
    radioWithProps,
    radioTabWithProps,
    datePickerWithProps,
    mediaUploaderWithProps,
    multiSelectWithProps,
  ];

  const formControlElement = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.props.id,
  );
  const finalId: string = React.isValidElement(formControlElement)
    ? (formControlElement.props.id || id).toString()
    : id;

  // Apply ID to the form control element
  const formControlWithProps = React.isValidElement(formControlElement)
    ? React.cloneElement(formControlElement as React.ReactElement, {
        id: finalId,
      })
    : null;

  const isInline = !!checkboxWithProps || !!radioWithProps;
  const formControl = formElements.find(Boolean) || formControlWithProps;
  const isValid =
    formElements.filter(Boolean).length === 1 || Boolean(formControl);

  if (!isValid)
    console.error(
      'You must pass a valid form element ex: Select, Input, Textarea, Checkbox, or an element with a passed in ID',
    );

  const renderFormElement = (
    <Styled.Wrapper>
      {leading && leading}
      {isValid && (
        <Styled.Control isInline={!!leading || !!trailing}>
          {formControl}
        </Styled.Control>
      )}
      {trailing && trailing}
    </Styled.Wrapper>
  );

  return (
    <Styled.FormControl disabled={disabled} isInline={isInline} {...rest}>
      <>
        {isInline ? (
          <Styled.InlineWrapper>
            {renderFormElement}
            {labelWithProps && (
              <Styled.FormLabel>{labelWithProps}</Styled.FormLabel>
            )}
          </Styled.InlineWrapper>
        ) : (
          <>
            {labelWithProps && (
              <Styled.FormLabel>{labelWithProps}</Styled.FormLabel>
            )}
            {!isInline && renderFormElement}
          </>
        )}

        <Styled.FormNotification>
          {showNotification && (
            <Notification
              intent={notificationIntent}
              {...(hasError ? { id: errorId } : {})}
            >
              {notificationMessage}
            </Notification>
          )}
        </Styled.FormNotification>
      </>
    </Styled.FormControl>
  );
};

export default Object.assign(FormControl, {
  Label: Label,
});
